import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeaderWithIcon,
  RadioBox,
  RadioGroup,
} from '@dev-spendesk/grapes';
import React, { useState } from 'react';

import {
  type AuthenticationMethod,
  type AuthenticationFactor,
  type AlternateAuthenticationFactor,
} from 'src/auth/modules/StrongCustomerAuthentication/models/factors';
import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { getAuthenticationMethodIcon } from './getAuthenticationMethodIcon';
import { getAuthenticationMethodTranslations } from './getTranslations';

type Props = {
  authenticationMethod?: AuthenticationMethod;
  factors: Array<AuthenticationFactor>;
  onClose: () => Promise<void>;
  onLostAccess: () => void;
  onNext: (authenticationMethod: AuthenticationMethod) => Promise<void>;
  showLostAccessLink: boolean;
};

export const AuthenticationMethodListModal = ({
  authenticationMethod,
  factors,
  onClose,
  onLostAccess,
  onNext,
  showLostAccessLink,
}: Props) => {
  const { t } = useTranslation('auth');

  const [selectedAuthenticationMethod, setSelectedAuthenticationMethod] =
    useState<AuthenticationMethod | undefined>(authenticationMethod);

  const handleConfirmSelectedAuthenticationMethod = async () => {
    if (selectedAuthenticationMethod) {
      await onNext(selectedAuthenticationMethod);
    }
  };

  const alternateAuthenticationFactors = factors.filter(
    (factor): factor is AlternateAuthenticationFactor => {
      return (
        factor.type === 'SMS' ||
        factor.type === 'EMAIL' ||
        factor.type === 'TOTP'
      );
    },
  );

  return (
    <ModalContent>
      <ModalHeaderWithIcon
        title={t('sca.alternateAuthenticationMethodModal.title')}
        subtitle={t('sca.alternateAuthenticationMethodModal.description')}
        iconName="lock-close"
        iconVariant="neutral"
        titleId="select-auth-factor-modal-title"
      />
      <ModalBody>
        <div className="mt-16">
          <RadioGroup
            name="authenticationFactorType"
            value={selectedAuthenticationMethod ?? null}
            direction="column"
            className="gap-8 text-start"
            onChange={(event) =>
              setSelectedAuthenticationMethod(
                event.target.value as AuthenticationMethod,
              )
            }
          >
            {alternateAuthenticationFactors.map((factor) => {
              const translations = getAuthenticationMethodTranslations(
                factor.type,
              );
              return (
                <RadioBox
                  key={factor.type}
                  label={t(translations.label)}
                  description={t(translations.description)}
                  value={factor.type}
                  iconName={getAuthenticationMethodIcon(factor.type)}
                />
              );
            })}
          </RadioGroup>
        </div>
        {showLostAccessLink && (
          <div className="mt-24 justify-self-start">
            <Button
              hasNoHorizontalPadding
              variant="tertiaryNeutral"
              text={t('sca.alternateAuthenticationMethodModal.lostAccess')}
              onClick={onLostAccess}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="secondaryNeutral"
          text={t('misc.cancel')}
          onClick={onClose}
        />
        <Button
          variant="primaryBrand"
          text={t('misc.continue')}
          isDisabled={!selectedAuthenticationMethod}
          onClick={handleConfirmSelectedAuthenticationMethod}
        />
      </ModalFooter>
    </ModalContent>
  );
};
